import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, KeyRound, Receipt, X } from 'lucide-react';
import { Buton, Etiket, Girdi, toast } from '@emanetx/ui';
import {
  kiralamaIadeBasla,
  kiralamaManuelAc,
  kiralamaZorlaSonlandir,
  type IadeSebebi,
  type KiralamaDetay,
} from '@emanetx/sdk';
import { istemci } from '../istemci.js';

export type AksiyonTipi = 'zorla' | 'manuel' | 'iade';

export interface KiralamaAksiyonDialogOzellikleri {
  acik: boolean;
  onDegisim: (acik: boolean) => void;
  kiralama: KiralamaDetay;
  tip: AksiyonTipi;
}

const SEBEP_SECENEKLERI: { deger: IadeSebebi; etiket: string }[] = [
  { deger: 'MUSTERI_TALEBI', etiket: 'Müşteri talebi' },
  { deger: 'TEKNIK_ARIZA', etiket: 'Teknik arıza' },
  { deger: 'ERKEN_IPTAL', etiket: 'Erken iptal' },
  { deger: 'ADMIN_DUZELTME', etiket: 'Admin düzeltme' },
  { deger: 'DIGER', etiket: 'Diğer' },
];

export function KiralamaAksiyonDialog({
  acik,
  onDegisim,
  kiralama,
  tip,
}: KiralamaAksiyonDialogOzellikleri) {
  const queryClient = useQueryClient();

  const [sebep, setSebep] = useState('');
  const [iadeMiktari, setIadeMiktari] = useState('');
  const [iadeSebebi, setIadeSebebi] = useState<IadeSebebi>('MUSTERI_TALEBI');
  const [iadeAciklama, setIadeAciklama] = useState('');

  useEffect(() => {
    if (acik) {
      setSebep('');
      setIadeMiktari(tip === 'iade' ? kiralama.odenenTutar : '');
      setIadeSebebi('MUSTERI_TALEBI');
      setIadeAciklama('');
    }
  }, [acik, tip, kiralama.odenenTutar]);

  const mutasyon = useMutation({
    mutationFn: async () => {
      if (tip === 'zorla') {
        return kiralamaZorlaSonlandir(istemci, kiralama.id, {
          sebep,
          ...(iadeMiktari && Number(iadeMiktari) > 0 && { iadeMiktari }),
        });
      }
      if (tip === 'manuel') {
        return kiralamaManuelAc(istemci, kiralama.id, { sebep });
      }
      return kiralamaIadeBasla(istemci, kiralama.id, {
        miktar: iadeMiktari,
        sebep: iadeSebebi,
        ...(iadeAciklama && { aciklama: iadeAciklama }),
      });
    },
    onSuccess: () => {
      toast.success(BASARI_MESAJI[tip]);
      void queryClient.invalidateQueries({ queryKey: ['kiralamalar'] });
      void queryClient.invalidateQueries({ queryKey: ['iadeler'] });
      onDegisim(false);
    },
    onError: (err: unknown) => {
      const mesaj =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'İşlem başarısız';
      toast.error(mesaj);
    },
  });

  const baslikMeta = META[tip];
  const sebepGecerli = sebep.trim().length >= 3;
  const iadeMiktarGecerli = /^\d+(\.\d{1,2})?$/.test(iadeMiktari) && Number(iadeMiktari) > 0;
  const formGecerli =
    tip === 'iade' ? iadeMiktarGecerli : tip === 'manuel' ? sebepGecerli : sebepGecerli;

  const sembol = kiralama.paraBirimi === 'TRY' ? '₺' : kiralama.paraBirimi === 'EUR' ? '€' : '$';

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start gap-4">
            <div
              className={`grid h-12 w-12 flex-shrink-0 place-items-center rounded-full ${baslikMeta.bg}`}
            >
              <baslikMeta.ikon className={baslikMeta.renk} size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-fume-900 dark:text-fume-50 text-base font-semibold">
                {baslikMeta.baslik}
              </Dialog.Title>
              <Dialog.Description className="text-fume-600 dark:text-fume-300 mt-1 text-sm">
                {baslikMeta.aciklama}{' '}
                <span className="text-marka-700 dark:text-marka-400 font-mono">{kiralama.kod}</span>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!formGecerli || mutasyon.isPending) return;
              mutasyon.mutate();
            }}
            className="mt-6 space-y-4"
          >
            {(tip === 'zorla' || tip === 'manuel') && (
              <div>
                <Etiket htmlFor="sebep">
                  Sebep <span className="text-red-500">*</span>
                </Etiket>
                <textarea
                  id="sebep"
                  value={sebep}
                  onChange={(e) => setSebep(e.target.value)}
                  rows={3}
                  required
                  minLength={3}
                  maxLength={200}
                  placeholder={
                    tip === 'zorla'
                      ? 'Örn. Müşteri 12 saattir teslim almadı, telefon kapalı.'
                      : 'Örn. Müşteri kapı sıkıştı diyor, manuel açma talep ediyor.'
                  }
                  className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
                />
                <p className="text-fume-400 mt-1 text-xs">
                  Bu sebep olay zaman çizelgesine ve denetim kaydına işlenir.
                </p>
              </div>
            )}

            {tip === 'zorla' && (
              <div>
                <Etiket htmlFor="zorlaIade">İade miktarı (opsiyonel)</Etiket>
                <div className="relative mt-1">
                  <span className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                    {sembol}
                  </span>
                  <Girdi
                    id="zorlaIade"
                    type="text"
                    inputMode="decimal"
                    value={iadeMiktari}
                    onChange={(e) => setIadeMiktari(e.target.value)}
                    placeholder="0.00"
                    className="pl-7"
                  />
                </div>
                <p className="text-fume-400 mt-1 text-xs">
                  Boş bırakırsanız iade kaydı oluşturulmaz. Ödenen tutar:{' '}
                  <span className="font-mono">
                    {sembol}
                    {kiralama.odenenTutar}
                  </span>
                </p>
              </div>
            )}

            {tip === 'iade' && (
              <>
                <div>
                  <Etiket htmlFor="miktar">
                    Miktar <span className="text-red-500">*</span>
                  </Etiket>
                  <div className="relative mt-1">
                    <span className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                      {sembol}
                    </span>
                    <Girdi
                      id="miktar"
                      type="text"
                      inputMode="decimal"
                      value={iadeMiktari}
                      onChange={(e) => setIadeMiktari(e.target.value)}
                      required
                      placeholder="0.00"
                      className="pl-7"
                    />
                  </div>
                  <p className="text-fume-400 mt-1 text-xs">
                    Ödenen toplam:{' '}
                    <span className="font-mono">
                      {sembol}
                      {kiralama.odenenTutar}
                    </span>
                  </p>
                </div>
                <div>
                  <Etiket htmlFor="iadeSebep">
                    Sebep <span className="text-red-500">*</span>
                  </Etiket>
                  <select
                    id="iadeSebep"
                    value={iadeSebebi}
                    onChange={(e) => setIadeSebebi(e.target.value as IadeSebebi)}
                    className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
                  >
                    {SEBEP_SECENEKLERI.map((s) => (
                      <option key={s.deger} value={s.deger}>
                        {s.etiket}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Etiket htmlFor="iadeAciklama">Açıklama (opsiyonel)</Etiket>
                  <textarea
                    id="iadeAciklama"
                    value={iadeAciklama}
                    onChange={(e) => setIadeAciklama(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="İç not, müşteriye gösterilmez."
                    className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  />
                </div>
              </>
            )}

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" boyut="orta" disabled={mutasyon.isPending}>
                  İptal
                </Buton>
              </Dialog.Close>
              <Buton
                stil={tip === 'zorla' ? 'tehlike' : 'birincil'}
                boyut="orta"
                type="submit"
                disabled={!formGecerli || mutasyon.isPending}
              >
                {mutasyon.isPending ? `${baslikMeta.onayMetni}...` : baslikMeta.onayMetni}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const META: Record<
  AksiyonTipi,
  {
    baslik: string;
    aciklama: string;
    onayMetni: string;
    ikon: typeof AlertTriangle;
    renk: string;
    bg: string;
  }
> = {
  zorla: {
    baslik: 'Kiralamayı zorla sonlandır',
    aciklama: 'Bu işlem kiralamayı kapatır, dolabı boşaltır.',
    onayMetni: 'Zorla sonlandır',
    ikon: AlertTriangle,
    renk: 'text-red-700 dark:text-red-400',
    bg: 'bg-red-100 dark:bg-red-900/30',
  },
  manuel: {
    baslik: 'Manuel dolap aç',
    aciklama: 'Dolaba açma komutu gönder.',
    onayMetni: 'Manuel aç',
    ikon: KeyRound,
    renk: 'text-marka-700 dark:text-marka-400',
    bg: 'bg-marka-100 dark:bg-marka-900/30',
  },
  iade: {
    baslik: 'İade başlat',
    aciklama: 'Yeni iade kaydı oluştur.',
    onayMetni: 'İade başlat',
    ikon: Receipt,
    renk: 'text-emerald-700 dark:text-emerald-400',
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
  },
};

const BASARI_MESAJI: Record<AksiyonTipi, string> = {
  zorla: 'Kiralama zorla sonlandırıldı',
  manuel: 'Manuel açma kaydedildi',
  iade: 'İade başlatıldı',
};
